import { DatePicker } from "@app/components/DatePicker.tsx";
import Kvp from "@app/components/kvp.tsx";
import SaveAndCloseOrAddButton from "@app/components/save-and-close-or-add-button.tsx";
import useAssignmentData from "@app/data-hooks/use-assignment-data.ts";
import useInvoiceManagementData from "@app/data-hooks/use-invoice-management-data.ts";
import { Box, Button, Stack, TextField } from "@mui/material";
import { useForm, useStore } from "@tanstack/react-form";
import { useNavigate } from "@tanstack/react-router";
import { QueryStatusRouter, Select } from "bi-lib";
import moment from "moment";
import { useRef } from "react";

interface Props {
    invoiceId?: number;
}

export default function InvoiceForm({ invoiceId }: Props) {
    const { invoice, insertInvoice, updateInvoice } = useInvoiceManagementData({ id: invoiceId });

    const navigate = useNavigate();

    const defaultValues: any = {
        assignmentId: invoice.data?.assignmentId,
        startDt: moment(invoice.data?.startDate ?? undefined),
        endDt: moment(invoice.data?.endDate ?? undefined),
        invoiceDt: moment(invoice.data?.invoiceDate ?? undefined),
        comment: invoice.data?.comment,
    };

    const form = useForm({
        defaultValues,
        onSubmit: async ({ value }) => {
            if (invoiceId != undefined) {
                await updateInvoice.mutateAsync({
                    invoiceId: invoiceId,
                    startDt: value.startDt?.format("MM/DD/YYYY"),
                    endDt: value.endDt?.format("MM/DD/YYYY"),
                    invoiceDt: value.invoiceDt?.format("MM/DD/YYYY"),
                    comment: value.comment,
                });
            } else {
                await insertInvoice.mutateAsync({
                    assignmentId: value.assignmentId,
                    startDt: value.startDt?.format("MM/DD/YYYY"),
                    endDt: value.endDt?.format("MM/DD/YYYY"),
                    invoiceDt: value.invoiceDt?.format("MM/DD/YYYY"),
                    invoiceTypeId:
                        invoice.data?.invoiceTypeId ?? assignmentInvoiceType.data?.invoiceTypeId ?? undefined,
                    comment: value.comment,
                });
            }
        },
    });

    const assignmentId = useStore(form.store, (state) => state.values.assignmentId);

    const { invoiceAssignmentsList, assignmentInvoiceType } = useAssignmentData({ id: assignmentId });

    const formRef = useRef<HTMLFormElement>(null);

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void form.handleSubmit();
            }}
            ref={formRef}
        >
            <QueryStatusRouter queryHook={invoice}>
                <Stack spacing={2}>
                    <QueryStatusRouter queryHook={invoiceAssignmentsList}>
                        <form.Field name={"assignmentId"}>
                            {(field) => (
                                <Select
                                    label={"Assignment"}
                                    value={field.state.value}
                                    onChange={(e) => field.handleChange(e)}
                                    options={invoiceAssignmentsList.data?.map((x) => ({
                                        label: x.displayName,
                                        value: x.assignmentId,
                                    }))}
                                    required
                                />
                            )}
                        </form.Field>
                    </QueryStatusRouter>

                    <Kvp keyTitle={"Invoice Type"}>
                        {invoice.data?.invoiceType ?? assignmentInvoiceType.data?.invoiceType}
                    </Kvp>
                    <form.Field name={"startDt"}>
                        {(field) => (
                            <DatePicker
                                value={field.state.value}
                                onChange={(e) => field.handleChange(e ?? field.state.value)}
                                label={"Start Date"}
                                required
                            />
                        )}
                    </form.Field>
                    <form.Field name={"endDt"}>
                        {(field) => (
                            <DatePicker
                                value={field.state.value}
                                onChange={(e) => field.handleChange(e ?? field.state.value)}
                                label={"End Date"}
                                required
                            />
                        )}
                    </form.Field>
                    <form.Field name={"invoiceDt"}>
                        {(field) => (
                            <DatePicker
                                value={field.state.value}
                                onChange={(e) => field.handleChange(e ?? field.state.value)}
                                label={"Invoice Date"}
                                required
                            />
                        )}
                    </form.Field>
                    <form.Field name={"comment"}>
                        {(field) => (
                            <TextField
                                value={field.state.value ?? ""}
                                onChange={(e) => field.handleChange(e.target.value)}
                                label={"Comment"}
                                minRows={3}
                                multiline
                            />
                        )}
                    </form.Field>
                </Stack>
            </QueryStatusRouter>
            <Box mt={2} display={"flex"} gap={1}>
                <Button onClick={() => navigate({ to: "/invoice" })} variant={"outlined"}>
                    Cancel
                </Button>
                <SaveAndCloseOrAddButton
                    onSave={form.handleSubmit}
                    onSecondary={
                        invoiceId == undefined
                            ? () => void navigate({ to: "/invoice/create" })
                            : () => void navigate({ to: "/invoice" })
                    }
                    secondaryActionTitle={invoiceId == undefined ? "Add New" : "Close"}
                    formRef={formRef}
                />
            </Box>
        </form>
    );
}
